"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseInclusiveDateBound = parseInclusiveDateBound;
exports.resolveFinmoTimeRangeForTimeline = resolveFinmoTimeRangeForTimeline;
exports.resolveFinmoTimeRangeForDateRange = resolveFinmoTimeRangeForDateRange;
exports.isWithinInclusiveDateRangeMs = isWithinInclusiveDateRangeMs;
exports.resolveWalletExportFetchStep = resolveWalletExportFetchStep;
const wallet_export_constants_js_1 = require("./wallet-export.constants.js");
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Parses an inclusive export/query bound.
 * Date-only end bounds (e.g. `"2026-09-13"`) extend to UTC end-of-day so same-day activity is not dropped.
 * Full ISO datetimes are used as-is (callers that want local calendar days should send local start/end ISO).
 */
function parseInclusiveDateBound(value, role = "start") {
    if (!value?.trim())
        return undefined;
    const trimmed = value.trim();
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime()))
        return undefined;
    if (role === "end" && DATE_ONLY_RE.test(trimmed)) {
        date.setUTCHours(23, 59, 59, 999);
    }
    return date;
}
function resolveFinmoTimeRangeForTimeline(timeline) {
    if (!timeline || timeline === "all") {
        return {};
    }
    const match = /^(\d{4})-(\d{2})$/.exec(timeline.trim());
    if (!match) {
        return {};
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) {
        return {};
    }
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0, 23, 59, 59, 999);
    return {
        start_time: Math.floor(start.getTime() / 1000),
        end_time: Math.floor(end.getTime() / 1000),
    };
}
/** Converts an optional ISO date/datetime range into the Finmo `start_time`/`end_time` unix-second params. An invalid or absent bound is simply omitted (left open-ended). */
function resolveFinmoTimeRangeForDateRange(startDate, endDate) {
    const range = {};
    const start = parseInclusiveDateBound(startDate, "start");
    if (start) {
        range.start_time = Math.floor(start.getTime() / 1000);
    }
    const end = parseInclusiveDateBound(endDate, "end");
    if (end) {
        range.end_time = Math.floor(end.getTime() / 1000);
    }
    return range;
}
/**
 * Inclusive millisecond range check for wallet PDF / list filtering.
 * Prefer this over comparing against `resolveFinmoTimeRangeForDateRange` unix seconds — Finmo
 * params are second-granularity, so flooring an end like `23:59:59.999` to `23:59:59.000` would
 * drop transactions in the final 999ms that still belong in the selected range.
 */
function isWithinInclusiveDateRangeMs(createdAtMs, startDate, endDate) {
    if (Number.isNaN(createdAtMs))
        return false;
    const start = parseInclusiveDateBound(startDate, "start");
    if (start && createdAtMs < start.getTime())
        return false;
    const end = parseInclusiveDateBound(endDate, "end");
    if (end && createdAtMs > end.getTime())
        return false;
    return true;
}
/**
 * Pagination stop / truncation rules for wallet PDF export fetch.
 * Truncation is reported for all-time and custom date-range exports (not calendar-month timelines).
 */
function resolveWalletExportFetchStep(input) {
    const maxRows = input.maxRows ?? wallet_export_constants_js_1.WALLET_EXPORT_MAX_ROWS;
    const reportsTruncation = !input.timeline || input.timeline === "all" || input.timeline === "custom";
    const nextTotal = input.rowsSoFar + input.pageRowCount;
    if (nextTotal >= maxRows) {
        return { stop: true, truncated: reportsTruncation, rowCap: maxRows };
    }
    if (!input.pageRowCount || input.pageRowCount < input.pageSize) {
        return { stop: true, truncated: false };
    }
    if (input.page + 1 > input.maxPages) {
        return { stop: true, truncated: reportsTruncation };
    }
    return { stop: false, truncated: false };
}
